import { useTranslate } from "@similarweb/i18n-provider";
import { Flex } from "@similarweb/ui-components/foundations";

import type { AccessibleItem } from "app/components/React/QuickSearch/pageData/filterAccessibleItems";
import { openLockedFeatureHook } from "app/components/React/QuickSearch/pageData/openLockedFeatureHook";
import { UnlockBadge } from "app/components/React/QuickSearch/UnlockBadge/UnlockBadge";

import { onboardingAgentTrackingService } from "../onboardingAgentTrackingService";
import { ONBOARDING_AGENT_MESSAGE_EXPLORE_NEXT } from "../texts";

import { goToSuggestedPage } from "./helpers/suggestedPageNavigation";
import type { OnboardingAgentAssistantExploreNextItem } from "./OnboardingAgentAssistantMessage.types";
import { ExploreNextLabel, ExploreNextSection, OnboardingAIChip } from "./styles";

type OnboardingAgentAssistantExploreNextProps = {
    messageIndex: number;
    items: readonly AccessibleItem<OnboardingAgentAssistantExploreNextItem>[];
    threadId?: string;
    onInAppQuickLinkLanding?: (pageTitle: string) => void;
};

export const OnboardingAgentAssistantExploreNext = ({
    messageIndex,
    items,
    threadId,
    onInAppQuickLinkLanding,
}: OnboardingAgentAssistantExploreNextProps) => {
    const { translate } = useTranslate();

    if (items.length === 0) {
        return null;
    }

    return (
        <ExploreNextSection data-automation={`onboarding-agent-message-${messageIndex}-explore-next`}>
            <ExploreNextLabel data-automation={`onboarding-agent-message-${messageIndex}-explore-next-label`}>
                {translate(ONBOARDING_AGENT_MESSAGE_EXPLORE_NEXT)}
            </ExploreNextLabel>
            <Flex
                flexWrap="wrap"
                gap="4px"
                width="100%"
                alignItems="center"
                data-automation={`onboarding-agent-message-${messageIndex}-explore-next-chips`}
            >
                {items.map(({ item, isLocked, hookKey }, chipIndex) => {
                    const automation = `onboarding-agent-message-${messageIndex}-explore-chip-${chipIndex}`;
                    const handleSuggestedPageClick = async (): Promise<void> => {
                        onboardingAgentTrackingService.trackQuickLinksClick({
                            clickedQuickLinks: `${item.label}: ${item.uiRouterState || item.link}`,
                            threadId,
                        });

                        if (isLocked) {
                            openLockedFeatureHook(item.uiRouterState, hookKey);
                            return;
                        }

                        const navigationResult = await goToSuggestedPage({
                            uiRouterState: item.uiRouterState,
                            link: item.link,
                            params: item.params,
                        });

                        if (navigationResult.type === "in-app" && navigationResult.didNavigate) {
                            onInAppQuickLinkLanding?.(item.label);
                        }
                    };

                    return (
                        <Flex
                            key={`${item.label}-${item.uiRouterState || item.link}-${chipIndex}`}
                            inline
                            alignItems="center"
                            gap="4px"
                            data-automation={automation}
                        >
                            <OnboardingAIChip
                                label={item.label}
                                variant="soft"
                                onClick={() => {
                                    void handleSuggestedPageClick();
                                }}
                                icon={isLocked ? <UnlockBadge /> : null}
                            />
                        </Flex>
                    );
                })}
            </Flex>
        </ExploreNextSection>
    );
};
